import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import ChevronDown from '@icon/ChevronDown';
import BottomSheet from '@comp/BottomSheet/BottomSheet';
import { Fonts, Spacings } from '@styles/index';

const DropdownRoot = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 7px ${Spacings.SPACING_4B};
  border-radius: 46px;
  border: 0.5px solid
    ${({ $hasSelection }) =>
      $hasSelection ? 'var(--color-accent-blue)' : '#e9eef2'};
  box-sizing: border-box;
  cursor: pointer;
  background: ${({ $hasSelection }) => ($hasSelection ? '#ddebfe' : '#ffffff')};
  font-size: 14px;
  line-height: 18px;
  color: ${({ $hasSelection }) =>
    $hasSelection ? 'var(--color-accent-blue)' : '#3c4852'};
  font-weight: ${({ $hasSelection }) =>
    $hasSelection ? Fonts.SEMIBOLD : Fonts.REGULAR};
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

const ChevronWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'none')};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Panel = styled.div`
  position: fixed;
  z-index: 1400;
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
  min-width: 290px;
  max-width: 320px;
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
  border-radius: 16px;
  background: var(--color-i-white);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
`;

const SheetContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
`;

const PanelBody = styled.div`
  max-height: 266px;
  overflow-y: auto;
`;

const Footer = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
`;

const ClearButton = styled.button`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 ${Spacings.SPACING_4B};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  font-size: 16px;
  line-height: 24px;
  font-weight: ${Fonts.SEMIBOLD};
  color: #607798;

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

const ApplyButton = styled.button`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 42px;
  padding: 0 ${Spacings.SPACING_4B};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: var(--color-accent-blue);
  font-size: 16px;
  line-height: 24px;
  font-weight: ${Fonts.BOLD};
  color: var(--color-i-white);

  &:focus-visible {
    outline: 2px solid var(--color-accent-blue);
    outline-offset: 2px;
  }
`;

const TestSeriesFilterDropdown = ({
  label,
  isOpen,
  hasSelection,
  onToggle,
  onClear,
  onApply,
  children,
  isMobile
}) => {
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const chevronColor = hasSelection ? 'var(--color-accent-blue)' : '#3c4852';

  const updatePanelPosition = useCallback(() => {
    if (!rootRef.current) return;

    const { bottom, left } = rootRef.current.getBoundingClientRect();
    setPanelPosition({ top: bottom + 11, left });
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isOpen || isMobile) return undefined;

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [isOpen, isMobile, updatePanelPosition]);

  useEffect(() => {
    if (!isOpen || isMobile) return undefined;

    const handleClickOutside = (event) => {
      if (
        rootRef.current?.contains(event.target) ||
        panelRef.current?.contains(event.target)
      ) {
        return;
      }

      onToggle(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile, onToggle]);

  const panelContent = (
    <>
      <PanelBody>{children}</PanelBody>
      <Footer>
        <ClearButton type="button" onClick={onClear}>
          Clear filters
        </ClearButton>
        <ApplyButton type="button" onClick={onApply}>
          Apply
        </ApplyButton>
      </Footer>
    </>
  );

  const desktopPanel =
    isMounted &&
    isOpen &&
    !isMobile &&
    createPortal(
      <Panel
        ref={panelRef}
        style={{ top: panelPosition.top, left: panelPosition.left }}
        role="dialog"
        aria-label={`${label} filters`}
      >
        {panelContent}
      </Panel>,
      document.body
    );

  return (
    <DropdownRoot ref={rootRef}>
      <Trigger
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        $hasSelection={hasSelection}
        onClick={() => onToggle(!isOpen)}
      >
        {label}
        <ChevronWrap $isOpen={isOpen}>
          <ChevronDown color={chevronColor} />
        </ChevronWrap>
      </Trigger>
      {desktopPanel}
      {isMounted && isMobile && isOpen && (
        <BottomSheet open={isOpen} onClose={() => onToggle(false)}>
          <SheetContent>{panelContent}</SheetContent>
        </BottomSheet>
      )}
    </DropdownRoot>
  );
};

export default TestSeriesFilterDropdown;

TestSeriesFilterDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  hasSelection: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  isMobile: PropTypes.bool
};

TestSeriesFilterDropdown.defaultProps = {
  isMobile: false
};
